import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to get suit icon path
const getSuitIcon = (suit) => {
  const suitLower = suit?.toLowerCase() || "";
  if (suitLower === "spade" || suitLower === "s") return "/assets/img/cardIcons/spade.png";
  if (suitLower === "heart" || suitLower === "h") return "/assets/img/cardIcons/heart.png";
  if (suitLower === "club" || suitLower === "c") return "/assets/img/cardIcons/club.png";
  if (suitLower === "diamond" || suitLower === "d") return "/assets/img/cardIcons/diamond.png";
  return null;
};

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;

  try {
    if (cardCode.length < 2) {
      return null;
    }

    const cleanCode = cardCode.replace(/\.(jpg|png)$/i, '');
    return `/assets/img/cards/${cleanCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to extract suit from card code
const getSuitFromCard = (cardCode) => {
  if (!cardCode) return null;
  const suit = cardCode.slice(-2);
  if (suit === "SS") return "spade";
  if (suit === "HH") return "heart";
  if (suit === "CC") return "club";
  if (suit === "DD") return "diamond";
  return null;
};

// Helper function to parse card string for Race20 and group by suit
const parseRace20CardsBySuit = (cardString) => {
  if (!cardString) return { suits: [] };

  const suits = {
    spade: { name: "Spade", icon: getSuitIcon("spade"), cards: [] },
    heart: { name: "Heart", icon: getSuitIcon("heart"), cards: [] },
    club: { name: "Club", icon: getSuitIcon("club"), cards: [] },
    diamond: { name: "Diamond", icon: getSuitIcon("diamond"), cards: [] }
  };

  const allItems = cardString.split(',').map(c => c.trim());
  const cardPattern = /^[2-9AJQK]?\d*[SDCH]{2}$/i;
  
  allItems.forEach((item) => {
    if (item && item !== '1' && cardPattern.test(item)) {
      const suit = getSuitFromCard(item);
      if (suit && suits[suit]) {
        suits[suit].cards.push({
          code: item,
          image: getCardImage(item)
        });
      }
    }
  });

  return {
    suits: [
      suits.spade,
      suits.heart,
      suits.club,
      suits.diamond
    ]
  };
};

// Map win value to suit name
const mapWinToSuit = (winValue) => {
  const win = winValue?.toString() || "0";
  switch (win) {
    case "1":
      return "spade";
    case "2":
      return "heart";
    case "3":
      return "club";
    case "4":
      return "diamond";
    default:
      return "";
  }
};

// Map win value to suit icon for last results
const mapWinToSuitIcon = (winValue) => {
  const suit = mapWinToSuit(winValue);
  return getSuitIcon(suit);
};

// Parse rdesc to extract winner, points, and cards
const parseRdesc = (rdesc) => {
  if (!rdesc) return { winner: "", points: "", cards: "" };
  
  const parts = rdesc.split('#');
  return {
    winner: parts[0] || "",
    points: parts[1] || "",
    cards: parts[2] || ""
  };
};

// Extract card code from winnat (e.g., "K Spade" -> "KSS")
const getWinningCardCode = (winnat, cardString) => {
  if (!winnat || !cardString) return null;
  
  const parts = winnat.split(' ');
  if (parts.length < 2) return null;
  
  const rank = parts[0].toUpperCase();
  const suitName = parts[1].toLowerCase();
  
  let suitCode = "";
  if (suitName === "spade") suitCode = "SS";
  else if (suitName === "heart") suitCode = "HH";
  else if (suitName === "club") suitCode = "CC";
  else if (suitName === "diamond") suitCode = "DD";
  else return null;
  
  const cardCode = `${rank}${suitCode}`;
  const allItems = cardString.split(',').map(c => c.trim());
  
  if (allItems.includes(cardCode)) {
    return cardCode;
  }
  
  return null;
};

const ResultRace20 = ({
  data = [],
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('race20');
        if (response?.success && response?.data?.data?.res) {
          setLastResults(response.data.data.res);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };

    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = useCallback(async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);

    const mid = resultItem?.mid;

    // Only fetch if we don't already have this detail result
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('race20', mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }

    setLoading(false);
    setShowModal(true);
  }, [detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    // Use detailResult if available
    const cardData = detailResult?.card ? parseRace20CardsBySuit(detailResult.card) : { suits: [] };
    const winValue = detailResult?.win || selectedResult.win;
    const winningSuit = mapWinToSuit(winValue);
    const winningCardCode = getWinningCardCode(detailResult?.winnat, detailResult?.card);
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : { winner: "", points: "", cards: "" };

    // Mark winning suit and separate winner card
    let winnerCard = null;
    const suits = cardData.suits.map((suit) => {
      const isWinner = suit.name.toLowerCase() === winningSuit;
      const cards = suit.cards.map((card) => {
        const isWinningCard = card.code === winningCardCode;
        if (isWinningCard) {
          winnerCard = { ...card, isWinningCard: true };
        }
        return {
          ...card,
          isWinningCard: false
        };
      });
      return {
        ...suit,
        isWinner: isWinner,
        cards: cards
      };
    });

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      suits: suits,
      winnerCard: winnerCard,
      winner: detailResult?.winnat || "",
      resultDetails: {
        winner: rdescData.winner || detailResult?.winnat || "",
        points: rdescData.points || "",
        cards: rdescData.cards || ""
      }
    };
  }, [selectedResult, detailResult, gameData, showModal]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0
    ? lastResults
    : data.map((res, idx) => ({ win: res, mid: null }));

  return (
    <>
      {/* Last Results Section */}
      <div className="casino-last-results">
        {allResults.map((resultItem, index) => {
          const suitIcon = mapWinToSuitIcon(resultItem.win);

          return (
            <span
              key={index}
              className="result"
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {suitIcon && <img src={suitIcon} alt={`Suit ${resultItem.win}`} />}
            </span>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Race 20 Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          {/* Race Result Box */}
          <div className="race-result-box">
            {modalData.suits.map((suit, index) => {
              // Find the King card for this suit
              const kingCard = suit.cards.find(card => {
                const rank = card.code?.charAt(0)?.toUpperCase();
                return rank === 'K';
              });
              
              // Regular cards (excluding King)
              const regularCards = suit.cards.filter(card => {
                const rank = card.code?.charAt(0)?.toUpperCase();
                return rank !== 'K';
              });

              return (
                <div key={index} className="pr mb-1">
                  {/* Suit Icon */}
                  {suit.icon && (
                    <span className="result-image">
                      <img src={suit.icon} alt={suit.name} />
                    </span>
                  )}
                  
                  {/* Regular Cards */}
                  {regularCards.map((card, cardIndex) => (
                    card.image && (
                      <span key={cardIndex} className="result-image">
                        <img
                          src={card.image}
                          alt={card.code}
                          onError={(e) => {
                            const cardName = card.image.split('/').pop();
                            const fallbackPath = `/assets/img/cards/${cardName}`;
                            if (e.target.src !== fallbackPath) {
                              e.target.src = fallbackPath;
                            } else {
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      </span>
                    )
                  ))}
                  
                  {/* King Card */}
                  {kingCard && kingCard.image && (
                    <span className="result-image k-image">
                      <img
                        src={kingCard.image}
                        alt={kingCard.code}
                        onError={(e) => {
                          const cardName = kingCard.image.split('/').pop();
                          const fallbackPath = `/assets/img/cards/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                      />
                    </span>
                  )}
                  
                  {/* Winner Icon for winning suit */}
                  {suit.isWinner && (
                    <div className="casino-result-cards-item">
                      <img 
                        src="https://d1arlbwbznybm5.cloudfront.net/v19/static/front/images/winner.png" 
                        className="winner-icon" 
                        alt="Winner"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Result Summary */}
          <p className="text-center mt-3">
            <b className="text-success">Result: </b>
            {modalData.resultDetails.winner && modalData.resultDetails.winner}
            {modalData.resultDetails.points && ` | Point: ${modalData.resultDetails.points}`}
            {modalData.resultDetails.cards && ` | Cards: ${modalData.resultDetails.cards}`}
          </p>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultRace20;

